import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { RetryOutcome } from '@aica/api-types';
import { FAILED_RECORD_STATUSES, isFailedRecordStatus } from '@/lib/records/status';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { DEMO_RECORD_NEXT } from '@/features/demo/demo-record-next';

/** Keyed by the shared failure set: a fifth failure status fails the build here. */
type FailedStatus = (typeof FAILED_RECORD_STATUSES)[number];

/**
 * Запасные строки — то, что i18next печатает, когда ключа в локали не нашлось.
 * Экспортируются ради собственного теста, как `HALTED_COPY`: через рендер при
 * полных бандлах до них не дойти.
 *
 * Причина названа ЭТАПОМ, на котором запись остановилась, а не текстом ошибки.
 * В `error_message` сбойной записи лежит сырое сообщение исключения или
 * провайдера — технический текст, в котором может оказаться что угодно, и
 * показывать его клиенту нельзя.
 */
export const FAILED_COPY: Record<
  FailedStatus,
  { title: string; body: string; next: string }
> = {
  transcription_failed: {
    title: 'Transcription failed',
    body: 'The recording was received, but it could not be turned into text — so there is no analysis either.',
    next: 'Retry processing — it picks up from this step. If it fails again, contact support.',
  },
  analysis_failed: {
    title: 'Analysis failed',
    body: 'The conversation was transcribed, but the analysis ended with an error.',
    next: 'Retry processing — the analysis runs again. If it fails again, contact support.',
  },
  // Not «CRM»: email and webhook targets end in the same status. And not
  // «nothing arrived»: the worker also lands here when a target succeeded and
  // saving the outcome threw — so the copy says the delivery is unconfirmed,
  // and asks to check before retrying, since a retry processes the record
  // again and delivers again (Codex on #1096). «As its project is set up»,
  // not «the analysis runs again»: with the project's analysis off the
  // automatic run has none (code review on #1264).
  delivery_failed: {
    title: 'Delivery not confirmed',
    body: 'Processing finished, but an error occurred while delivering the result — it may not have reached the CRM, email or webhook.',
    next: 'If the result is not there, retry processing: the record is processed again as its project is set up, and the result is delivered again.',
  },
  failed: {
    title: 'Processing failed',
    body: 'The record stopped at one of the processing steps.',
    next: 'Retry processing: the record is processed again as its project is set up — without a transcript the recording is transcribed again — and the result is delivered again. If it fails again, contact support.',
  },
};

/**
 * The next step when the retry button is not there — the per-status «retry
 * processing» would point at an action this viewer does not have (Codex on
 * #1096). Exported for the same reason as FAILED_COPY.
 */
export const FAILED_NEXT_WITHOUT_RETRY = {
  noRight:
    'An organization administrator can retry processing. If it keeps failing, contact support.',
  noSource:
    'There is nothing to retry from — no text, transcript or recording the processing could start from was saved. Contact support if this conversation should have one.',
  needsStart:
    'Here a retry would leave the record waiting for a manual start, and only a client or admin can start it.',
  needsText:
    'Its text was never saved, so a manual start would have nothing to send. The chat stays as it is.',
} as const;

/** The next step when the retry only puts the record back to wait for a start. */
export const FAILED_NEXT_WAITS =
  'Retry puts the record back to wait for a manual start: nothing runs and no minutes are spent until it is started.';

interface RecordFailedNoticeProps {
  /** `record.status`. Anything outside the failure set renders nothing. */
  status?: string | null;
  className?: string;
  /**
   * Право повторить обработку. Сервер требует `record.update`; вызывающий
   * решает с запасом в безопасную сторону — кто-то не увидит кнопку, но никто
   * не увидит кнопку, которая ответит 403.
   */
  canRetry?: boolean;
  /**
   * Есть ли из чего повторять — транскрипт или аудиофайл, та же проверка, что
   * у сервера. `false` — повтор невозможен ни для кого, и следующий шаг
   * говорит об этом, а не о кнопке.
   */
  hasRetrySource: boolean;
  /**
   * Известны ли уже право и источник. Пока нет — следующий шаг не
   * называется вовсе: «повторять не из чего» или «повторить может
   * администратор» до ответа были бы догадкой.
   */
  actionKnown?: boolean;
  /**
   * Что сделает повтор — решение маршрута повтора, известное до нажатия
   * (`record.retryOutcome`). `needs_start`/`needs_text` — сервер откажет:
   * кнопки нет, следующий шаг называет причину. `waits` — кнопка есть, но
   * обещает ожидание запуска, а не обработку. Нет значения — обычный текст.
   */
  retryOutcome?: RetryOutcome;
  /** Повторяет обработку ЭТОЙ записи (`retry-transcription`). */
  onRetry?: () => void;
  /** Идёт запуск повтора — кнопка блокируется, чтобы не задвоить запрос. */
  isRetrying?: boolean;
  /**
   * Демо-запись (#1503): повтор её не берёт (источника нет, отбор исключает
   * `is_demo`) — вместо шага «повторите» или «обратитесь в поддержку»
   * объяснение, и кнопки нет.
   */
  isDemo?: boolean;
}

/**
 * Why an opened record shows neither transcript nor analysis when processing
 * failed (#1096).
 *
 * The panels used to fall back to "not available yet — processing may still be
 * in progress" for these statuses too. A record three days into
 * `transcription_failed` told the customer it was still being processed — a
 * delay to wait out rather than a failure to act on. The notice says what
 * happened, at which step, and the one move that changes it.
 */
export function RecordFailedNotice({
  status,
  className,
  canRetry,
  hasRetrySource,
  actionKnown = true,
  retryOutcome,
  onRetry,
  isRetrying,
  isDemo = false,
}: RecordFailedNoticeProps) {
  const { t } = useTranslation('records');

  if (!isFailedRecordStatus(status)) return null;

  const failed = status as FailedStatus;
  const { title, body, next } = FAILED_COPY[failed];
  const key = `records.failedNotice.${failed}`;
  const refused = retryOutcome === 'needs_start' || retryOutcome === 'needs_text';
  const offersRetry =
    !isDemo && Boolean(canRetry) && hasRetrySource && Boolean(onRetry) && !refused;
  const nextText = isDemo
    ? t('records.demoRecordNext', DEMO_RECORD_NEXT)
    : offersRetry
      ? retryOutcome === 'waits'
        ? t('records.failedNotice.nextWaits', FAILED_NEXT_WAITS)
        : t(`${key}.next`, next)
      : !hasRetrySource
        ? t('records.failedNotice.nextNoSource', FAILED_NEXT_WITHOUT_RETRY.noSource)
        : canRetry && retryOutcome === 'needs_start'
          ? t(
              'records.failedNotice.retry.needsStartMessage',
              FAILED_NEXT_WITHOUT_RETRY.needsStart
            )
          : canRetry && retryOutcome === 'needs_text'
            ? t(
                'records.failedNotice.retry.needsTextMessage',
                FAILED_NEXT_WITHOUT_RETRY.needsText
              )
            : t('records.failedNotice.nextNoRight', FAILED_NEXT_WITHOUT_RETRY.noRight);

  return (
    <div
      data-testid="record-failed-notice"
      className={cn('rounded-card border border-danger/30 bg-danger-dim p-4', className)}
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className="h-5 w-5 flex-shrink-0 text-danger" aria-hidden="true" />
        <div className="space-y-1.5">
          <p className="text-sm font-semibold text-fg">{t(`${key}.title`, title)}</p>
          <p className="text-xs leading-relaxed text-fg-secondary">
            {t(`${key}.body`, body)}
          </p>
          {actionKnown && (
            <p className="text-xs leading-relaxed text-fg-muted">{nextText}</p>
          )}
          {actionKnown && offersRetry && (
            <Button size="sm" onClick={onRetry} isLoading={isRetrying} className="mt-1">
              {t('records.failedNotice.retry.action', 'Retry processing')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
